import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import api, { BACKEND_URL } from './api';

/**
 * One live connection to Laravel Reverb per browser tab, opened after login.
 * Host and scheme follow the API's address; the public app key and port come from
 * REACT_APP_REVERB_APP_KEY and REACT_APP_REVERB_PORT (see frontend/.env.example).
 *
 * The connection state is shared so screens can fall back to polling while it is down.
 */

const backend = new URL(BACKEND_URL);
const scheme = process.env.REACT_APP_REVERB_SCHEME || backend.protocol.replace(':', '');
const host = process.env.REACT_APP_REVERB_HOST || backend.hostname;
const port = Number(process.env.REACT_APP_REVERB_PORT || (scheme === 'https' ? 443 : 8080));
const key = process.env.REACT_APP_REVERB_APP_KEY;

let echo = null;
let status = 'idle'; // idle | connecting | connected | unavailable
const listeners = new Set();

function setStatus(next) {
  if (next === status) return;
  status = next;
  listeners.forEach((listener) => listener(status));
}

export function getRealtimeStatus() {
  return status;
}

export function subscribeRealtimeStatus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The Echo instance, connecting on first use. Null when live updates are not configured. */
export function getEcho() {
  if (echo) return echo;
  if (!key) {
    setStatus('unavailable');
    return null;
  }

  window.Pusher = Pusher;
  echo = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: scheme === 'https',
    enabledTransports: scheme === 'https' ? ['wss'] : ['ws'],
    // Private channels are authorised with the API's Sanctum token, through the same axios client.
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        api.post('/broadcasting/auth', { socket_id: socketId, channel_name: channel.name })
          .then((response) => callback(null, response.data))
          .catch((error) => callback(error, null));
      },
    }),
  });

  const connection = echo.connector.pusher.connection;
  setStatus('connecting');
  connection.bind('state_change', ({ current }) => {
    if (current === 'connected') setStatus('connected');
    else if (current === 'connecting' || current === 'initialized') setStatus('connecting');
    else setStatus('unavailable'); // unavailable, failed, disconnected
  });

  return echo;
}

/** Close the connection, on logout. */
export function disconnectRealtime() {
  if (echo) {
    echo.disconnect();
    echo = null;
  }
  setStatus('idle');
}
